import { useState, useEffect } from 'react';
import { Tooltip, Typography, useTheme } from '@mui/material';
import FileDownloadOffIcon from '@mui/icons-material/FileDownloadOff';
import { storageUnitCache } from 'util/cache/storageUnitCacheIndexedDB';
import { useOnlineStatus } from 'hooks/useOnlineStatus';
import { useStorageUnits } from 'contexts/storageUnitContext';
import DownloadOfflineIcon from 'icons/DownloadOfflineIcon';
import { hasOfflineFileData } from 'store/storageunits/utils';

type Props = {
  fileId: string;
  showPlaceholder?: boolean;
  hasLocalFileData?: boolean;
};

enum DownloadStatus {
  CHECKING = 'CHECKING',
  NOT_CACHED = 'NOT_CACHED',
  DOWNLOADING = 'DOWNLOADING',
  CACHED = 'CACHED',
}

const OfflineStatusIndicator = ({
  fileId,
  showPlaceholder = false,
  hasLocalFileData = false,
}: Props) => {
  const theme = useTheme();
  const isOnline = useOnlineStatus();
  const { downloadingFiles } = useStorageUnits();
  const [status, setStatus] = useState<DownloadStatus>(DownloadStatus.CHECKING);
  const isDownloading = downloadingFiles.has(fileId);
  const onlineKey = isOnline ? 'online' : 'offline';

  useEffect(() => {
    let mounted = true;

    const checkCacheStatus = async () => {
      if (hasLocalFileData) {
        setStatus(DownloadStatus.CACHED);
        return;
      }

      try {
        const cached = await storageUnitCache.getOne(fileId);
        if (!mounted) return;

        if (hasOfflineFileData(cached)) {
          setStatus(DownloadStatus.CACHED);
        } else {
          setStatus(DownloadStatus.NOT_CACHED);
        }
      } catch {
        if (mounted) {
          setStatus(DownloadStatus.NOT_CACHED);
        }
      }
    };

    void checkCacheStatus();

    return () => {
      mounted = false;
    };
  }, [fileId, hasLocalFileData, isDownloading, onlineKey]);

  const currentStatus = isDownloading ? DownloadStatus.DOWNLOADING : status;

  const getIcon = () => {
    switch (currentStatus) {
      case DownloadStatus.DOWNLOADING:
        return null;
      case DownloadStatus.CACHED:
        return (
          <DownloadOfflineIcon
            sx={{ fontSize: 24, color: theme.palette.grey[900] }}
          />
        );
      case DownloadStatus.NOT_CACHED:
        if (!isOnline) {
          return (
            <FileDownloadOffIcon
              sx={{ height: 24, width: 24, color: theme.palette.grey[800] }}
            />
          );
        }
        return null;
      case DownloadStatus.CHECKING:
      default:
        return null;
    }
  };

  const getTooltip = () => {
    switch (currentStatus) {
      case DownloadStatus.CACHED:
        return 'Available offline';
      case DownloadStatus.NOT_CACHED:
        if (!isOnline) {
          return 'Not available offline';
        }
        return '';
      default:
        return '';
    }
  };

  if (currentStatus === DownloadStatus.CHECKING) {
    return null;
  }

  const icon = getIcon();

  if (!icon) {
    if (showPlaceholder) {
      return <Typography>-</Typography>;
    }
    return null;
  }

  return (
    <Tooltip title={getTooltip()}>
      <span
        aria-label={getTooltip()}
        style={{ display: 'inline-flex', padding: 0, color: 'inherit' }}
      >
        {icon}
      </span>
    </Tooltip>
  );
};

export default OfflineStatusIndicator;
